import { useSuspenseQuery } from '@tanstack/react-query';
import { satisfies } from 'semver';
import { z } from 'zod';
import { hostQueryKeys } from '#api';
import { sendCommand } from '#api/hosts.ts';
import { DEBUG_MIN_VERSION } from '../constants.ts';

/** {@link CommandError} describes error messages that may be returned from dnclient as a result of successful communication over the API. */
export class CommandError extends Error {
  constructor(response: { error: string }) {
    super(response.error);
    this.name = 'CommandError';
  }
}

const commandsListSchema = z
  .object({
    commands: z.array(z.string()),
  })
  .or(z.object({ error: z.string() }));

type CommandsListResponse = z.infer<typeof commandsListSchema>;

const oldOrMobileResult = { commands: [] } satisfies CommandsListResponse;

/** {@link useSuspenseDNClientCommandsQuery} returns what commands the host supports, manually filling in the values for hosts that don't support ListCommand */
export function useSuspenseDNClientCommandsQuery(
  hostID: string,
  hostVersion: string | null,
  hostPlatform: string | null
) {
  return useSuspenseQuery({
    queryKey: [...hostQueryKeys.detail(hostID), { hostVersion, hostPlatform }, 'dnclientCommands'],
    queryFn: async () => {
      const isMobile = hostPlatform === 'mobile';
      const isTooOld = Boolean(hostVersion && satisfies(hostVersion, `< ${DEBUG_MIN_VERSION}`));

      // If the dnclient doesn't support any commands. If we are missing metadata, we fail open, and allow attempting to send commands to dnclient.
      if (isMobile || isTooOld) {
        return oldOrMobileResult;
      }

      const result = commandsListSchema.parse(await sendCommand(hostID, 'ListCommands'));

      // If we get a successful response that contained an error, wrap it in a `CommandError`
      if ('error' in result) {
        throw new CommandError(result);
      }

      return result;
    },
  });
}
